import { useSettingsStore } from '@/features/settings/stores/settings.store';
import { logger } from '@/features/help/utils/logger';

/**
 * Bank Statement Analysis Settings component
 * Controls whether we trigger analysis + whether we cap billed PDFs at 4 per upload.
 */
export const BankStatementAnalysisSettings = () => {
  const {
    bankStatementAnalysisEnabled,
    bankStatementAnalysisLastFourMonths,
    updateBankStatementAnalysisSettings,
    isSavingBankStatementAnalysis,
  } = useSettingsStore();

  /**
   * Toggle analysis enablement (and persist)
   */
  const handleToggleEnabled = async () => {
    const newEnabled = !bankStatementAnalysisEnabled;
    const nextSettings = {
      bankStatementAnalysisEnabled: newEnabled,
      // If disabling analysis, we force the second toggle off in the backend as well.
      bankStatementAnalysisLastFourMonths: newEnabled ? bankStatementAnalysisLastFourMonths : false,
    };

    logger.info('BankStatementAnalysisSettings', 'Toggling bank statement analysis', {
      currentEnabled: bankStatementAnalysisEnabled,
      newEnabled,
      nextSettings,
    });

    await updateBankStatementAnalysisSettings(nextSettings);
  };

  /**
   * Toggle "last 4 months" (billing cap) setting (and persist)
   */
  const handleToggleLastFourMonths = async () => {
    const newValue = !bankStatementAnalysisLastFourMonths;
    const nextSettings = {
      bankStatementAnalysisEnabled,
      bankStatementAnalysisLastFourMonths: newValue,
    };

    logger.info('BankStatementAnalysisSettings', 'Toggling bank statement analysis billing cap', {
      currentValue: bankStatementAnalysisLastFourMonths,
      newValue,
      enabled: bankStatementAnalysisEnabled,
    });

    await updateBankStatementAnalysisSettings(nextSettings);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Bank Statement Analysis</h2>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Enable bank statement analysis</h3>
            <p className="text-sm text-gray-500">
              When enabled, we send uploaded PDFs to AhiDoc for analysis on every document upload.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleEnabled}
            disabled={isSavingBankStatementAnalysis}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              bankStatementAnalysisEnabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            role="switch"
            aria-checked={bankStatementAnalysisEnabled}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                bankStatementAnalysisEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Analyze last 4 months (billing cap)</h3>
            <p className="text-sm text-gray-500">
              When enabled, billing is capped at 4 PDFs per upload even if more PDFs are analyzed.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleLastFourMonths}
            disabled={isSavingBankStatementAnalysis || !bankStatementAnalysisEnabled}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              bankStatementAnalysisLastFourMonths ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            role="switch"
            aria-checked={bankStatementAnalysisLastFourMonths}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                bankStatementAnalysisLastFourMonths ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

